import React, { useEffect, useRef, useState } from "react";

import ModalConfirm from "components/ModalConfirm/ModalConfirm";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { ReactComponent as Character } from "assets/illustrations/character.svg";
import { ReactComponent as Accueil } from "assets/illustrations/accueil.svg";
import { ReactComponent as Export } from "assets/illustrations/export.svg";
import { ReactComponent as ReleaseKeyCard } from "assets/illustrations/release_keycard.svg";
import { ReactComponent as History } from "assets/illustrations/history.svg";
import { ReactComponent as Visite } from "assets/illustrations/visite.svg";
import { ReactComponent as Planned } from "assets/illustrations/planned.svg";
import { PrinterOutlined } from "@ant-design/icons";

import ModalPrinterError from "components/Printer/Modal/modalError";
import ModalPrinterStatus from "components/Printer/Modal/modalStatus";
import ModalPrinterIPDifference from "components/Printer/Modal/modalIPDifference";
import { usePromptModal } from "components/Modal/PromptModal";

import Tile from "common/Tile/Tile";

import "./mainPage.less";

import {
  analyseCode,
  associateKeyCard,
  createVisitForBadgeLost,
  getKeyCardOwner,
  initializePrinter,
  isVisitValid,
  markBadgeCheckin,
  markBadgeCheckout,
  printerErrorIgnored,
  releaseKeyCards,
  selecteDefaultPrinter,
  startFeching,
  stopFeching,
  updateCurrentResident,
  updateVisitsFromUser,
} from "services/features/VisitsSlice";
import { QRCodeReader, QRCODE } from "helpers";
import { Button, message, Typography } from "antd";
import useWindowFocus from "use-window-focus";
import { useConfirmModal } from "components/Modal/ConfirmModal";

const MainPage = () => {
  const { printer, appName, useKeyCard } = useSelector(state => state.visits);
  const [showPrinterStatus, setShowPrinterStatus] = useState(false);
  const [showModalConfirm, setShowModalConfirm] = useState(false);
  const [titleModalConfirm, setTitleModalConfirm] = useState("");
  const [lastQRCodeRead, setLastQRCodeRead] = useState("");
  const [questionModalConfirm, setQuestionModalConfirm] = useState("");
  const [questionModalConfirmOkOnly, setQuestionModalConfirmOkOnly] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [askUserInput, PromptModal] = usePromptModal();
  const [askUserConfirm, ConfirmModal] = useConfirmModal();

  const useKeyCardRef = useRef(useKeyCard);

  const [t] = useTranslation();
  const dispatch = useDispatch();
  const history = useHistory();

  const qrCodeReader = useRef(QRCodeReader());

  const windowFocused = useWindowFocus();

  const { disablePrinter } = useSelector(state => state.visits);

  useEffect(() => {
    qrCodeReader.current.setListener(handleQRRead);
    setIsVisible(true);
    let reader = qrCodeReader.current;

    return () => {
      setIsVisible(false);
      reader.stop();
      reader.clearListener();
    };
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    useKeyCardRef.current = useKeyCard;
  }, [useKeyCard]);

  useEffect(() => {
    if (isVisible && windowFocused) {
      qrCodeReader.current.start();
    } else {
      qrCodeReader.current.stop();
    }
  }, [windowFocused, isVisible]);

  const modalConfirmHandler = useRef({
    onOk: () => {},
    onCancel: () => {},
  });

  const askQuestion = (title, question, okOnly = false) =>
    new Promise(resolve => {
      qrCodeReader.current.stop();
      setTitleModalConfirm(title);
      setQuestionModalConfirm(question);
      setQuestionModalConfirmOkOnly(okOnly);
      setShowModalConfirm(true);
      modalConfirmHandler.current.onOk = () => {
        qrCodeReader.current.start();
        resolve(true);
      };
      modalConfirmHandler.current.onCancel = () => {
        qrCodeReader.current.start();
        resolve(false);
      };
    });

  const handleQRRead = async value => {
    if (value.includes("::")) value = value.split("::")[0];
    const qrCode = await dispatch(analyseCode(value));
    if (!qrCode) {
      setLastQRCodeRead(value + " : unkown");
      return message.error(t("unreadableQRCode"));
    }

    switch (qrCode.type) {
      case QRCODE.TYPES.VISITOR_TODAY_VISIT:
        try {
          dispatch(startFeching());
          const validity = await dispatch(
            isVisitValid(qrCode.infos.visit.id, qrCode.infos.visitor.id),
          );

          if (!validity.isValid && validity.reason === "canceled") {
            return message.error(t("VisitCanceled"));
          }
          if (!validity.isValid && validity.reason === "expired") {
            return message.error(t("VisitExpired"));
          }
        } catch (error) {
          return message.error(t("CantVerifyVisitValidity"));
        } finally {
          dispatch(stopFeching());
        }
        setLastQRCodeRead(value + " : visitor");
        dispatch(updateCurrentResident(qrCode.infos.visit.resident));
        history.push({
          pathname: `/meetingPage/${qrCode.infos.visit.resident.id}`,
          state: {
            currentVisitor: qrCode.infos.visitor,
            knownVisit: qrCode.infos.visit,
            goBackToHome: true,
            useKeyCard: useKeyCardRef.current,
          },
        });
        break;
      case QRCODE.TYPES.BADGE:
        setLastQRCodeRead(value + " : badge");

        if (qrCode.infos.status === "checkin") {
          if (
            await askQuestion(
              "Sortie visiteur",
              `Voulez-vous enregistrer la sortie du visiteur ${qrCode.infos.visitor.firstname} ${qrCode.infos.visitor.lastname} ?`,
            )
          ) {
            try {
              if (useKeyCardRef.current && qrCode.infos.keyCard) {
                if (
                  await askUserConfirm(t("KeyCardToReturn"), t("KeyCardToReturnText", qrCode.infos))
                ) {
                  await dispatch(releaseKeyCards([qrCode.infos.keyCard], false));
                  message.info(t("KeyCardReleased", { reference: qrCode.infos.keyCard }));
                }
              }

              await dispatch(markBadgeCheckout(value));
              message.info(t("ExitConfirmation"));
            } catch (error) {
              message.error("ExitError");
            }
          }
        }
        if (qrCode.infos.status === "checkout") {
          if (useKeyCardRef.current && qrCode.infos.keyCard) {
            if (
              await askUserConfirm(t("KeyCardToReturn"), t("KeyCardToReturnText", qrCode.infos))
            ) {
              await dispatch(releaseKeyCards([qrCode.infos.keyCard], false));
              message.info("KeyCardReleased", { reference: qrCode.infos.keyCard });
            }
          } else
            await askQuestion(
              "Badge périmé",
              `La sortie du visiteur ${qrCode.infos.visitor.firstname} ${qrCode.infos.visitor.lastname} a déjà été enregistrée.`,
              true,
            );
        }
        if (qrCode.infos.status === "registered") {
          if (
            await askQuestion(
              "Entrée d'un visiteur",
              `Avez-vous vérifié l'identité de M. ou Mme ${qrCode.infos.visitor.firstname} ${qrCode.infos.visitor.lastname}.`,
            )
          ) {
            if (useKeyCardRef.current) {
              let badgeRef;
              let noKeycard = false;

              while (!badgeRef && !noKeycard) {
                badgeRef = await askUserInput(t("BadgeNumber"), t("EnterBadgeNumber"));

                if (!badgeRef) {
                  noKeycard = true;
                } else {
                  const { owner } = await dispatch(getKeyCardOwner(badgeRef));
                  if (owner && owner.id !== qrCode.infos.visitor.id) {
                    if (
                      (await askUserConfirm(
                        t("BadgeAlreadyUsed"),
                        t("BadgeAlreadyUsedText", { ...owner, reference: badgeRef }),
                      )) === false
                    ) {
                      badgeRef = null;
                    }
                  }
                }
              }
              await dispatch(associateKeyCard(value, badgeRef));
            }

            dispatch(markBadgeCheckin(value));
            message.info(t("EntryConfirmation"));
          }
        }

        break;

      case QRCODE.TYPES.RESIDENT:
        setLastQRCodeRead(value + " : resident");
        const userId = qrCode.infos.id;
        await dispatch(updateVisitsFromUser(userId));

        await dispatch(updateCurrentResident(Object.assign(qrCode.infos, { id: userId })));
        const visit = await dispatch(createVisitForBadgeLost(userId));
        history.push({
          pathname: `/meetingPage/${userId}`,
          state: { type: "resident", printVisitBadgeLost: visit.id, goBackToHome: true },
        });

        break;
      case QRCODE.TYPES.OUTDATED:
        message.warning(t("OutdatedQRCode"));
        break;
      default:
        setLastQRCodeRead(value + " : unknown");

        message.error(t("unreadableQRCode"));
        break;
    }
  };

  return (
    <>
      {PromptModal}
      {ConfirmModal}
      <p className="mainPage__texte">
        <ModalConfirm
          okOnly={questionModalConfirmOkOnly}
          onConfirm={v => {
            setShowModalConfirm(false);
            modalConfirmHandler.current.onOk(v);
          }}
          onCancel={v => {
            setShowModalConfirm(false);
            modalConfirmHandler.current.onCancel(v);
          }}
          title={titleModalConfirm}
          question={questionModalConfirm}
          isVisible={showModalConfirm}
        />
        <ModalPrinterError
          isVisible={printer.isError && !printer.errorIgnored}
          onIgnore={() => dispatch(printerErrorIgnored())}
          onRetry={() => dispatch(initializePrinter())}
          error={printer.error}
        />
        <ModalPrinterStatus
          isVisible={showPrinterStatus}
          onClose={() => setShowPrinterStatus(false)}
        />
        <ModalPrinterIPDifference
          isVisible={
            !process.env.REACT_APP_DEV &&
            printer.isReady &&
            printer.ipAddress !== printer.requiredIp
          }
          ipConfigured={printer.requiredIp}
          ipFound={printer.ipAddress}
          onClose={() => dispatch(selecteDefaultPrinter())}
        />
        <Character className="mainPage__texte__img" />
        <span className="mainPage__texte__hello">{t("Hello")},</span> <br />
        <span className="mainPage__texte__subTitle">
          {t("CanYouShowMeYour")} <strong>QRCode ?</strong>
        </span>
        <br /> {t("YouReceivedItBy")} <strong>{t("mail")}</strong> {t("OrOnYour")} <br />
        <strong>
          {" "}
          {t("MobileApp1")} {appName} {t("MobileApp2")}
        </strong>
      </p>

      <section className="homePage__section">
        <Tile href="/whoAreYou">
          <Accueil style={{ height: "75px", marginTop: "20px" }} />
          <Typography.Title level={2} style={{ fontSize: 25 }}>
            {t("VisitorReceptionNoQRCode")}
          </Typography.Title>
        </Tile>

        <Tile href="/visitPlanned">
          <Planned style={{ height: "75px", marginTop: "20px" }} />
          <Typography.Title level={2}>{t("PlannedVisit")}</Typography.Title>
        </Tile>

        <Tile href="/visitDay">
          <Visite style={{ height: "75px", marginTop: "20px" }} />
          <Typography.Title level={2}>{t("VisitOfTheDay")}</Typography.Title>
        </Tile>

        <Tile
          href="/extract"
          style={{
            display: "flex",
            alignItems: "center",
            flexFlow: "column",
            justifyContent: "center",
          }}
        >
          <Export style={{ height: "75px", marginTop: "20px" }} />
          <Typography.Title level={2}>{t("ExtractVisit")}</Typography.Title>
        </Tile>

        {useKeyCard && (
          <Tile
            href="/list-keycard"
            style={{
              display: "flex",
              alignItems: "center",
              flexFlow: "column",
              justifyContent: "center",
            }}
          >
            <ReleaseKeyCard style={{ height: "75px", marginTop: "20px" }} />
            <Typography.Title level={2}>{t("ManageKeyCards")}</Typography.Title>
          </Tile>
        )}
        <Tile
          href="/history-visitor"
          style={{
            display: "flex",
            alignItems: "center",
            flexFlow: "column",
            justifyContent: "center",
          }}
        >
          <History style={{ height: "75px", marginTop: "20px" }} />
          <Typography.Title level={2}>{t("VisitorHistory")}</Typography.Title>
        </Tile>
      </section>

      <div className="homePage__footer">
        {!disablePrinter && (
          <Button
            shape="circle"
            icon={<PrinterOutlined />}
            onClick={() => setShowPrinterStatus(true)}
          />
        )}
      </div>
    </>
  );
};

export default MainPage;
